/**
 * js/hawksoft-link.js — the ONE HawkSoft deep-link builder.
 *
 * There used to be four independent copies of this logic (js/cgl-utils.js,
 * js/policy-lookup.js, js/call-logger.js, js/dashboard-widgets.js), all
 * emitting the same two URL shapes. They drifted: A-476's iPad fix landed in
 * cgl-utils.js only, so Personal Renewals, Voicemail, Call Hub, the HawkSoft
 * Logger and the dashboard widget all kept handing iPads a dead `hs://` link.
 * Every builder now forwards here, so a format change is one edit.
 *
 * Loads EAGERLY from index.html, before dashboard-widgets.js — the compliance
 * widget links client names on a cold dashboard, before any plugin's lazy
 * `scripts:` run. Same rule as cgl-utils.js / folder-ics.js / dashboard-icons.js.
 *
 * Deliberately dependency-free (no Utils, no STORAGE_KEYS): it returns a
 * descriptor, never HTML, so escaping stays at the call site where the
 * surrounding markup is known. That also lets it load before anything else.
 *
 * POLICY-LEVEL LINKS ARE NOT SUPPORTED YET — `policyUrl()` returns null on
 * purpose. See the comment on that function before changing it.
 */
window.HawkSoftLink = (() => {
    'use strict';

    // Web Agent Portal. The desktop CMS has no URL bar and is reached through
    // its `hs://` protocol handler instead.
    const AGENT_PORTAL_CLIENT = 'https://agents.hawksoft.app/client/';

    function _ambient(prop, fallback) {
        try {
            const nav = (typeof navigator !== 'undefined') ? navigator : null;
            return nav && nav[prop] !== undefined ? nav[prop] : fallback;
        } catch (_) { return fallback; }
    }

    /**
     * A-476. iPadOS 13+ reports a desktop "Macintosh" user agent, so the UA
     * regex alone handed iPads the hs:// desktop protocol — a dead link, since
     * no HawkSoft desktop app exists there. Touch capability on a "Mac" is the
     * reliable tell (real Macs report maxTouchPoints 0).
     *
     * Both arguments are optional and fall back to `navigator` INDEPENDENTLY,
     * so a caller passing only a UA string (the legacy
     * PolicyLookup.buildHawksoftUrl signature) still gets the iPad check.
     */
    function isMobileDevice(userAgent, maxTouchPoints) {
        try {
            const ua = String(userAgent != null ? userAgent : _ambient('userAgent', ''));
            if (/Android|iPhone|iPad|iPod|webOS/i.test(ua)) return true;
            const touch = maxTouchPoints != null ? maxTouchPoints : _ambient('maxTouchPoints', 0);
            return /Macintosh/i.test(ua) && typeof touch === 'number' && touch > 1;
        } catch (_) { return false; }
    }

    /**
     * Link descriptor for a HawkSoft CLIENT.
     *
     * @param {string|number} clientNumber - HawkSoft client number (`hawksoftId`).
     * @param {object} [opts]
     * @param {string} [opts.userAgent]      - override for testing.
     * @param {number} [opts.maxTouchPoints] - override for testing.
     * @param {string} [opts.subject]        - noun for the title, e.g. 'client'
     *                                         → "Open client in HawkSoft".
     * @returns {{href:string,title:string,target:string,rel:string,isMobile:boolean}|null}
     *          null when there is no id — callers render plain text.
     */
    function clientUrl(clientNumber, opts) {
        const o = opts || {};
        const id = String(clientNumber == null ? '' : clientNumber).trim();
        if (!id) return null;
        const isMobile = isMobileDevice(o.userAgent, o.maxTouchPoints);
        const subject = o.subject ? ` ${String(o.subject).trim()}` : '';
        return {
            href: isMobile
                ? `${AGENT_PORTAL_CLIENT}${encodeURIComponent(id)}`
                : `hs://${encodeURIComponent(id)}`,
            title: isMobile
                ? `Open${subject} in HawkSoft Agent Portal`
                : `Open${subject} in HawkSoft`,
            target: isMobile ? '_blank' : '',
            rel: isMobile ? 'noopener' : '',
            isMobile,
        };
    }

    /**
     * Link descriptor for a specific POLICY — currently always null.
     *
     * Two formats have been ruled out, both by field test, both recorded in
     * CHANGELOG.md (June 24, 2026):
     *   1. `hs://<client>/policy/<id>` — the desktop CMS recognises the
     *      sub-route, tries to resolve it, then silently aborts: it indexes its
     *      own local policy ids, which are not in the partner-API data we
     *      ingest.
     *   2. `agents.hawksoft.app/client/<c>/policy/<p>` — never anything but a
     *      guess. It shipped here for a year as a dormant helper with zero call
     *      sites, which is the only reason it never sent anyone to a 404.
     *
     * A third format is under investigation — `client-viewer.hawksoft.app/
     * hs-redirects?v=1&c=…&p=…&a=…&t=…&n=…`, whose `c` param is derivable from
     * the client number but whose `p` and `t` params are not yet understood.
     * Returning null until that is verified is deliberate: a wrong policy link
     * is worse than none, because the agent cannot tell "opened the wrong
     * record" from "opened the right one" without reading the whole screen.
     *
     * Callers must handle null by falling back to the client link or to plain
     * text — never by constructing a URL themselves.
     */
    function policyUrl(/* clientNumber, policyId, opts */) {
        return null;
    }

    return {
        AGENT_PORTAL_CLIENT,
        isMobileDevice,
        clientUrl,
        policyUrl,
    };
})();
